const createEmptyFormState = () => ({ data: {}, valid: {}, meta: {}, valueLabels: {} });
import { isValuePresent } from '@/composables/formRegistry.js';
import Vue from 'vue';

const defaultValueByType = type => {
  if (type === 'file' || type === 'checkboxGroup') {
    return [];
  }
  if (type === 'choice' || type === 'radio' || type === 'select') {
    return null;
  }
  return '';
};

export default {
  namespaced: true,
  state: () => ({
    forms: {}
  }),
  mutations: {
    ensureForm(state, { formName }) {
      if (!state.forms[formName]) {
        Vue.set(state.forms, formName, createEmptyFormState());
      }
    },
    setMeta(state, { formName, fieldName, meta }) {
      const formState = state.forms[formName];
      if (!formState) {
        return;
      }
      formState.meta[fieldName] = meta;
    },
    setValueLabels(state, { formName, fieldName, labels }) {
      const formState = state.forms[formName];
      if (!formState || !fieldName) {
        return;
      }
      if (!formState.valueLabels) {
        Vue.set(formState, 'valueLabels', {});
      }
      Vue.set(formState.valueLabels, fieldName, { ...labels });
    },
    initializeFields(state, { formName, fields }) {
      const formState = state.forms[formName];
      if (!formState) {
        return;
      }

      fields.forEach(field => {
        if (!Object.prototype.hasOwnProperty.call(formState.data, field.name)) {
          const fallback =
            field.initialValue !== undefined ? field.initialValue : defaultValueByType(field.type);
          Vue.set(formState.data, field.name, fallback);
        }
        if (!formState.meta.required) {
          Vue.set(formState.meta, 'required', {});
        }
        if (!Object.prototype.hasOwnProperty.call(formState.meta.required, field.name)) {
          Vue.set(formState.meta.required, field.name, !!field.required);
        }
        if (!Object.prototype.hasOwnProperty.call(formState.valid, field.name)) {
          Vue.set(formState.valid, field.name, field.required ? false : true);
        }
      });
    },
    setValue(state, { formName, fieldName, value }) {
      const formState = state.forms[formName];
      if (!formState) {
        return;
      }
      if (value === undefined) {
        return;
      }

      if (!Object.prototype.hasOwnProperty.call(formState.data, fieldName)) {
        Vue.set(formState.data, fieldName, value);
      } else {
        formState.data[fieldName] = value;
      }

      const required = !!formState.meta.required?.[fieldName];
      const nextValid = required ? isValuePresent(value) : true;

      if (!Object.prototype.hasOwnProperty.call(formState.valid, fieldName)) {
        Vue.set(formState.valid, fieldName, nextValid);
      } else {
        formState.valid[fieldName] = nextValid;
      }
    },

    setValidity(state, { formName, fieldName, isValid }) {
      const formState = state.forms[formName];
      if (!formState) {
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(formState.valid, fieldName)) {
        Vue.set(formState.valid, fieldName, !!isValid);
      } else {
        formState.valid[fieldName] = !!isValid;
      }
    },
    reset(state, { formName, fields = [] }) {
      if (!state.forms[formName]) {
        Vue.set(state.forms, formName, createEmptyFormState());
      }
      const nextState = createEmptyFormState();

      fields.forEach(field => {
        if (!field || !field.name) {
          return;
        }
        const fallback =
          field.initialValue !== undefined ? field.initialValue : defaultValueByType(field.type);
        Vue.set(nextState.data, field.name, fallback);
        Vue.set(nextState.valid, field.name, field.required ? false : true);
        if (!nextState.meta.required) {
          Vue.set(nextState.meta, 'required', {});
        }
        Vue.set(nextState.meta.required, field.name, !!field.required);
      });

      Vue.set(state.forms, formName, nextState);
    }
  },
  getters: {
    formData: state => formName => state.forms[formName]?.data || {},
    formValidity: state => formName => state.forms[formName]?.valid || {},
    formValueLabels: state => formName => state.forms[formName]?.valueLabels || {},
    stepIsValid: state => (formName, fields) => {
      const validity = state.forms[formName]?.valid || {};
      return fields.filter(field => !field.hidden).every(field => !!validity[field.name]);
    },
    formIsValid: state => (formName, allFields) => {
      const validity = state.forms[formName]?.valid || {};
      return allFields.filter(field => !field.hidden).every(field => !!validity[field.name]);
    },
    formMeta: state => formName => state.forms[formName]?.meta || {}
  }
};
